import { useState, useEffect, useCallback, useRef } from 'react';

/**
 * Hook personalizado para manejar llamadas a la API con estado de carga y errores
 * @param {Function} apiFunction - Función de API a llamar
 * @param {Array} dependencies - Dependencias para re-ejecutar la llamada
 * @param {Object} options - Opciones adicionales
 * @returns {Object} - {data, loading, error, refetch}
 */
export const useApiCall = (apiFunction, dependencies = [], options = {}) => {
  const {
    immediate = true,
    onSuccess = null,
    onError = null,
    initialData = null
  } = options;

  const [data, setData] = useState(initialData);
  const [loading, setLoading] = useState(immediate);
  const [error, setError] = useState(null);
  const isMountedRef = useRef(true);

  const execute = useCallback(async (...args) => {
    try {
      setLoading(true);
      setError(null);
      
      const result = await apiFunction(...args);
      
      if (isMountedRef.current) {
        setData(result);
        if (onSuccess) onSuccess(result);
      }
      
      return result;
    } catch (err) {
      if (isMountedRef.current) {
        setError(err);
        if (onError) onError(err);
      }
      throw err;
    } finally {
      if (isMountedRef.current) {
        setLoading(false);
      }
    }
  }, [apiFunction, onSuccess, onError]);

  useEffect(() => {
    isMountedRef.current = true;

    if (immediate) {
      execute();
    } else {
      setLoading(false);
    }

    return () => {
      isMountedRef.current = false;
    };
  }, [...dependencies, immediate]); // eslint-disable-line react-hooks/exhaustive-deps

  const refetch = useCallback(() => execute(), [execute]);

  return { data, loading, error, refetch, execute };
};

/**
 * Hook para manejar múltiples llamadas a la API en paralelo
 * @param {Array<Function>} apiFunctions - Array de funciones de API
 * @param {Array} dependencies - Dependencias para re-ejecutar
 * @returns {Object} - {data, loading, error, refetch}
 */
export const useParallelApiCalls = (apiFunctions, dependencies = []) => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const isMountedRef = useRef(true);

  const execute = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      
      const results = await Promise.all(
        apiFunctions.map(fn => fn())
      );
      
      if (isMountedRef.current) {
        setData(results);
      }
      
      return results;
    } catch (err) {
      if (isMountedRef.current) {
        setError(err);
      }
      throw err;
    } finally {
      if (isMountedRef.current) {
        setLoading(false);
      }
    }
  }, [apiFunctions]);

  useEffect(() => {
    isMountedRef.current = true;
    execute();

    return () => {
      isMountedRef.current = false;
    };
  }, dependencies); // eslint-disable-line react-hooks/exhaustive-deps

  const refetch = useCallback(() => execute(), [execute]);

  return { data, loading, error, refetch };
};

/**
 * Hook para ejecutar una llamada API solo cuando se cumpla una condición
 * @param {Function} apiFunction - Función de API
 * @param {boolean} condition - Condición para ejecutar
 * @param {Array} dependencies - Dependencias
 * @returns {Object} - {data, loading, error, refetch}
 */
export const useConditionalApiCall = (apiFunction, condition, dependencies = []) => {
  return useApiCall(
    apiFunction,
    [...dependencies, condition],
    { immediate: condition }
  );
};
